import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  Detection,
  DetectionObject,
  DetectionObjectType,
} from '../types';
import api from '../services/api';

// Constants
const THEME_COLOR = '#9c27b0';
const CORNER_SIZE = 10;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;

// Color mapping for object types
const OBJECT_TYPE_COLORS: Record<DetectionObjectType, string> = {
  vehicle: '#1976d2',
  bike: '#4caf50',
  plate: '#ff9800',
  hazard: '#f44336',
};

const OBJECT_TYPES: DetectionObjectType[] = ['vehicle', 'bike', 'plate', 'hazard'];

interface DetectionObjectEditorProps {
  open: boolean;
  detection: Detection;
  imageUrl: string;
  onClose: () => void;
  onObjectsUpdated: (detectionId: string, objects: DetectionObject[]) => void;
  onFinish?: () => void; // Called when 'F' key is pressed (for task context - marks as completed)
}

interface DragState {
  type: 'corner' | 'create' | null;
  objectId?: string;
  cornerIndex?: number; // 0=TL, 1=TR, 2=BR, 3=BL
  startX?: number;
  startY?: number;
}

const DetectionObjectEditor: React.FC<DetectionObjectEditorProps> = ({
  open,
  detection,
  imageUrl,
  onClose,
  onObjectsUpdated,
  onFinish,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [objects, setObjects] = useState<DetectionObject[]>([]);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [dragState, setDragState] = useState<DragState>({ type: null });
  const [creatingObject, setCreatingObject] = useState(false);
  const [newObjectType, setNewObjectType] = useState<DetectionObjectType>('vehicle');
  const [newBoxPreview, setNewBoxPreview] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingAngle, setEditingAngle] = useState(false);
  const [previewAngle, setPreviewAngle] = useState<number | null>(null);
  const lastDetectionIdRef = useRef<string | null>(null);

  // Initialize objects from detection - only reset selection when dialog opens or detection changes
  useEffect(() => {
    if (open && detection) {
      const isNewDetection = lastDetectionIdRef.current !== detection.id;

      if (isNewDetection) {
        // New detection or dialog just opened - reset everything
        setObjects([...detection.objects]);
        setSelectedObjectId(null);
        setZoom(1);
        setPanOffset({ x: 0, y: 0 });
        setCreatingObject(false);
        setEditingAngle(false);
        setPreviewAngle(null);
        setError(null);
        lastDetectionIdRef.current = detection.id;
      }
      // If same detection, objects are managed internally - don't reset from props
    }

    if (!open) {
      lastDetectionIdRef.current = null;
    }
  }, [open, detection]);

  // Reset angle editing when selection changes
  useEffect(() => {
    setEditingAngle(false);
    setPreviewAngle(null);
  }, [selectedObjectId]);

  // Track container size for canvas
  useEffect(() => {
    if (!open || !containerRef.current) return;

    const updateSize = () => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);

      if (width > 0 && height > 0) {
        setCanvasSize({ width, height });
      }
    };

    // Initial size after a brief delay to ensure layout is complete
    const timeoutId = setTimeout(updateSize, 50);

    const resizeObserver = new ResizeObserver(() => {
      updateSize();
    });

    resizeObserver.observe(containerRef.current);
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [open]);

  // Load image with proper cleanup to prevent memory leaks
  useEffect(() => {
    if (!open || !imageUrl) return;

    let isMounted = true;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (isMounted) {
        setImage(img);
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      }
    };
    img.onerror = () => {
      if (isMounted) {
        setError('Failed to load image');
      }
    };
    img.src = imageUrl;

    return () => {
      isMounted = false;
      img.onload = null;
      img.onerror = null;
    };
  }, [open, imageUrl]);

  // Fit image to canvas when both are ready
  useEffect(() => {
    if (!image || imageSize.width === 0 || canvasSize.width === 0) return;

    // Calculate zoom to fit image in canvas
    const scaleX = canvasSize.width / imageSize.width;
    const scaleY = canvasSize.height / imageSize.height;
    const fitZoom = Math.min(scaleX, scaleY, MAX_ZOOM);

    // Center the image
    const scaledWidth = imageSize.width * fitZoom;
    const scaledHeight = imageSize.height * fitZoom;
    const offsetX = (canvasSize.width - scaledWidth) / 2;
    const offsetY = (canvasSize.height - scaledHeight) / 2;

    setZoom(fitZoom);
    setPanOffset({ x: offsetX, y: offsetY });
  }, [image, imageSize.width, imageSize.height, canvasSize.width, canvasSize.height]);

  // Draw canvas with viewport-style zoom and pan
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas to fixed container size
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    // Clear with background
    ctx.fillStyle = '#333';
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

    // Apply transformations: translate for pan, then scale for zoom
    ctx.save();
    ctx.translate(panOffset.x, panOffset.y);
    ctx.scale(zoom, zoom);

    // Draw image at origin (transformations handle positioning)
    ctx.drawImage(image, 0, 0, imageSize.width, imageSize.height);

    // Draw objects (coordinates in image space)
    objects.forEach((obj) => {
      if (!obj.box || !Array.isArray(obj.box) || obj.box.length < 4) {
        return;
      }
      const [cx, cy, w, h] = obj.box;
      const pixelX = (cx - w / 2) * imageSize.width;
      const pixelY = (cy - h / 2) * imageSize.height;
      const pixelW = w * imageSize.width;
      const pixelH = h * imageSize.height;

      const color = OBJECT_TYPE_COLORS[obj.type] || '#ffffff';
      const isSelected = obj.id === selectedObjectId;

      // Adjust line width to account for zoom (keep visual thickness consistent)
      const adjustedLineWidth = (isSelected ? 3 : 2) / zoom;
      const adjustedCornerSize = CORNER_SIZE / zoom;

      // Draw box
      ctx.strokeStyle = color;
      ctx.lineWidth = adjustedLineWidth;
      ctx.strokeRect(pixelX, pixelY, pixelW, pixelH);

      // Draw selection highlight
      if (isSelected) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1 / zoom;
        ctx.setLineDash([4 / zoom, 4 / zoom]);
        ctx.strokeRect(pixelX - 2 / zoom, pixelY - 2 / zoom, pixelW + 4 / zoom, pixelH + 4 / zoom);
        ctx.setLineDash([]);

        // Draw corner handles
        const corners = [
          { x: pixelX, y: pixelY },
          { x: pixelX + pixelW, y: pixelY },
          { x: pixelX + pixelW, y: pixelY + pixelH },
          { x: pixelX, y: pixelY + pixelH },
        ];

        corners.forEach((corner) => {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(
            corner.x - adjustedCornerSize / 2,
            corner.y - adjustedCornerSize / 2,
            adjustedCornerSize,
            adjustedCornerSize
          );
          ctx.strokeStyle = color;
          ctx.lineWidth = 2 / zoom;
          ctx.strokeRect(
            corner.x - adjustedCornerSize / 2,
            corner.y - adjustedCornerSize / 2,
            adjustedCornerSize,
            adjustedCornerSize
          );
        });
      }

      // Draw type label
      const fontSize = 12 / zoom;
      ctx.font = `${fontSize}px sans-serif`;
      const labelWidth = ctx.measureText(obj.type).width + 6 / zoom;
      ctx.fillStyle = color;
      ctx.fillRect(pixelX, pixelY - 16 / zoom, labelWidth, 16 / zoom);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(obj.type, pixelX + 3 / zoom, pixelY - 4 / zoom);

      // Draw angle arrow for vehicle/bike objects
      if (obj.type === 'vehicle' || obj.type === 'bike') {
        // Use preview angle if editing this object, otherwise use saved angle
        const angleToShow = (isSelected && editingAngle && previewAngle !== null)
          ? previewAngle
          : obj.angle;

        if (angleToShow !== null && angleToShow !== undefined) {
          const centerX = cx * imageSize.width;
          const centerY = cy * imageSize.height;
          const arrowLength = Math.min(pixelW, pixelH) * 0.4;
          const lineWidth = 2 / zoom;
          const headSize = 8 / zoom;

          drawArrow(ctx, centerX, centerY, angleToShow, arrowLength, lineWidth, headSize, color);
        }
      }
    });

    // Draw new box preview (in image space)
    if (newBoxPreview) {
      // Convert canvas coords to image coords for preview
      const toImageX = (canvasX: number) => (canvasX - panOffset.x) / zoom;
      const toImageY = (canvasY: number) => (canvasY - panOffset.y) / zoom;

      const imgX1 = toImageX(newBoxPreview.x1);
      const imgY1 = toImageY(newBoxPreview.y1);
      const imgX2 = toImageX(newBoxPreview.x2);
      const imgY2 = toImageY(newBoxPreview.y2);

      const minX = Math.min(imgX1, imgX2);
      const minY = Math.min(imgY1, imgY2);
      const boxW = Math.abs(imgX2 - imgX1);
      const boxH = Math.abs(imgY2 - imgY1);

      ctx.strokeStyle = OBJECT_TYPE_COLORS[newObjectType];
      ctx.lineWidth = 2 / zoom;
      ctx.setLineDash([6 / zoom, 3 / zoom]);
      ctx.strokeRect(minX, minY, boxW, boxH);
      ctx.setLineDash([]);
    }

    ctx.restore();
  }, [image, imageSize, canvasSize, zoom, panOffset, objects, selectedObjectId, newBoxPreview, newObjectType, editingAngle, previewAngle]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Get mouse position relative to canvas (in canvas coordinates)
  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  // Convert canvas coordinates to image coordinates
  const canvasToImage = (canvasX: number, canvasY: number): { x: number; y: number } => {
    return {
      x: (canvasX - panOffset.x) / zoom,
      y: (canvasY - panOffset.y) / zoom,
    };
  };

  // Calculate angle from object center to mouse position (0=right, 90=up)
  const calculateAngleFromCenter = (mouseCanvasX: number, mouseCanvasY: number, obj: DetectionObject): number => {
    const [cx, cy] = obj.box;
    const centerPixelX = cx * imageSize.width;
    const centerPixelY = cy * imageSize.height;
    const mouseImg = canvasToImage(mouseCanvasX, mouseCanvasY);

    const dx = mouseImg.x - centerPixelX;
    const dy = centerPixelY - mouseImg.y; // Invert Y (screen coords are upside down)

    let angle = Math.atan2(dy, dx) * (180 / Math.PI);
    if (angle < 0) angle += 360;

    return Math.round(angle);
  };

  // Draw an arrow from start point in the given angle direction
  const drawArrow = (
    ctx: CanvasRenderingContext2D,
    startX: number,
    startY: number,
    angle: number,
    length: number,
    lineWidth: number,
    headSize: number,
    color: string
  ) => {
    // Convert angle to radians (0=right, 90=up in our convention)
    const radians = (angle * Math.PI) / 180;
    const endX = startX + length * Math.cos(radians);
    const endY = startY - length * Math.sin(radians); // Invert Y for screen coords

    // Draw arrow line with dark outline for visibility
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = lineWidth + 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // Draw arrowhead
    const headAngle = Math.atan2(endY - startY, endX - startX);

    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - (headSize + 1) * Math.cos(headAngle - Math.PI / 6),
      endY - (headSize + 1) * Math.sin(headAngle - Math.PI / 6)
    );
    ctx.lineTo(
      endX - (headSize + 1) * Math.cos(headAngle + Math.PI / 6),
      endY - (headSize + 1) * Math.sin(headAngle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - headSize * Math.cos(headAngle - Math.PI / 6),
      endY - headSize * Math.sin(headAngle - Math.PI / 6)
    );
    ctx.lineTo(
      endX - headSize * Math.cos(headAngle + Math.PI / 6),
      endY - headSize * Math.sin(headAngle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fill();
  };

  // Check if mouse is over a corner handle (takes canvas coords)
  const getCornerAtPosition = (
    canvasX: number,
    canvasY: number,
    obj: DetectionObject
  ): number | null => {
    const imgPos = canvasToImage(canvasX, canvasY);

    const [cx, cy, w, h] = obj.box;
    const pixelX = (cx - w / 2) * imageSize.width;
    const pixelY = (cy - h / 2) * imageSize.height;
    const pixelW = w * imageSize.width;
    const pixelH = h * imageSize.height;

    const corners = [
      { x: pixelX, y: pixelY },
      { x: pixelX + pixelW, y: pixelY },
      { x: pixelX + pixelW, y: pixelY + pixelH },
      { x: pixelX, y: pixelY + pixelH },
    ];

    // Adjust hit area based on zoom
    const hitSize = CORNER_SIZE / zoom;

    for (let i = 0; i < corners.length; i++) {
      const corner = corners[i];
      if (
        Math.abs(imgPos.x - corner.x) <= hitSize &&
        Math.abs(imgPos.y - corner.y) <= hitSize
      ) {
        return i;
      }
    }
    return null;
  };

  // Find object with closest corner to mouse position (takes canvas coords)
  const getObjectAtPosition = (canvasX: number, canvasY: number): DetectionObject | null => {
    const imgPos = canvasToImage(canvasX, canvasY);

    let closestObject: DetectionObject | null = null;
    let closestDistance = Infinity;

    objects.forEach((obj) => {
      if (!obj.box || obj.box.length < 4) return;

      const [cx, cy, w, h] = obj.box;
      const pixelX = (cx - w / 2) * imageSize.width;
      const pixelY = (cy - h / 2) * imageSize.height;
      const pixelW = w * imageSize.width;
      const pixelH = h * imageSize.height;

      // Get all 4 corners
      const corners = [
        { x: pixelX, y: pixelY },
        { x: pixelX + pixelW, y: pixelY },
        { x: pixelX + pixelW, y: pixelY + pixelH },
        { x: pixelX, y: pixelY + pixelH },
      ];

      // Find minimum distance to any corner
      corners.forEach((corner) => {
        const dist = Math.sqrt(
          Math.pow(imgPos.x - corner.x, 2) + Math.pow(imgPos.y - corner.y, 2)
        );
        if (dist < closestDistance) {
          closestDistance = dist;
          closestObject = obj;
        }
      });
    });

    // Only return if click is reasonably close (within a threshold based on zoom)
    const maxDistance = 100 / zoom; // Adjust threshold based on zoom level
    return closestDistance <= maxDistance ? closestObject : null;
  };

  // Mouse down handler
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);

    // Middle mouse button (wheel click) - start panning
    if (e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      return;
    }

    // Left click only for other actions
    if (e.button !== 0) return;

    // Finalize angle editing on click
    if (editingAngle && previewAngle !== null) {
      handleAngleChange(previewAngle);
      setEditingAngle(false);
      setPreviewAngle(null);
      return;
    }

    if (creatingObject) {
      // Start drawing new box (in canvas coords, will convert in drawCanvas)
      setDragState({ type: 'create', startX: pos.x, startY: pos.y });
      setNewBoxPreview({ x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
      return;
    }

    // Check if clicking on corner of selected object
    if (selectedObjectId) {
      const selectedObj = objects.find((o) => o.id === selectedObjectId);
      if (selectedObj) {
        const cornerIndex = getCornerAtPosition(pos.x, pos.y, selectedObj);
        if (cornerIndex !== null) {
          setDragState({
            type: 'corner',
            objectId: selectedObjectId,
            cornerIndex,
            startX: pos.x,
            startY: pos.y,
          });
          return;
        }
      }
    }

    // Check if clicking on an object
    const clickedObj = getObjectAtPosition(pos.x, pos.y);
    if (clickedObj) {
      setSelectedObjectId(clickedObj.id);
    } else {
      setSelectedObjectId(null);
    }
  };

  // Mouse move handler
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Handle panning
    if (isPanning) {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }

    const pos = getMousePos(e);

    // Handle angle editing mode
    if (editingAngle && selectedObjectId) {
      const selectedObj = objects.find((o) => o.id === selectedObjectId);
      if (selectedObj && (selectedObj.type === 'vehicle' || selectedObj.type === 'bike')) {
        const angle = calculateAngleFromCenter(pos.x, pos.y, selectedObj);
        setPreviewAngle(angle);
      }
      return;
    }

    if (dragState.type === 'create' && dragState.startX !== undefined && dragState.startY !== undefined) {
      setNewBoxPreview({
        x1: dragState.startX,
        y1: dragState.startY,
        x2: pos.x,
        y2: pos.y,
      });
      return;
    }

    if (dragState.type === 'corner' && dragState.objectId && dragState.cornerIndex !== undefined) {
      // Convert canvas position to image coordinates and clamp to image bounds
      const rawImgPos = canvasToImage(pos.x, pos.y);
      const imgPos = {
        x: Math.max(0, Math.min(imageSize.width, rawImgPos.x)),
        y: Math.max(0, Math.min(imageSize.height, rawImgPos.y)),
      };

      setObjects((prev) =>
        prev.map((obj) => {
          if (obj.id !== dragState.objectId) return obj;

          const [cx, cy, w, h] = obj.box;
          let pixelX = (cx - w / 2) * imageSize.width;
          let pixelY = (cy - h / 2) * imageSize.height;
          let pixelW = w * imageSize.width;
          let pixelH = h * imageSize.height;

          // Adjust based on which corner is being dragged (in image coordinates)
          switch (dragState.cornerIndex) {
            case 0: // TL
              pixelW += pixelX - imgPos.x;
              pixelH += pixelY - imgPos.y;
              pixelX = imgPos.x;
              pixelY = imgPos.y;
              break;
            case 1: // TR
              pixelW = imgPos.x - pixelX;
              pixelH += pixelY - imgPos.y;
              pixelY = imgPos.y;
              break;
            case 2: // BR
              pixelW = imgPos.x - pixelX;
              pixelH = imgPos.y - pixelY;
              break;
            case 3: // BL
              pixelW += pixelX - imgPos.x;
              pixelX = imgPos.x;
              pixelH = imgPos.y - pixelY;
              break;
          }

          // Ensure minimum size (in image pixels)
          const minSize = 10;
          if (pixelW < minSize) pixelW = minSize;
          if (pixelH < minSize) pixelH = minSize;

          // Convert back to proportional
          const newW = pixelW / imageSize.width;
          const newH = pixelH / imageSize.height;
          const newCx = (pixelX / imageSize.width) + newW / 2;
          const newCy = (pixelY / imageSize.height) + newH / 2;

          // Clamp to valid range
          const clampedCx = Math.max(newW / 2, Math.min(1 - newW / 2, newCx));
          const clampedCy = Math.max(newH / 2, Math.min(1 - newH / 2, newCy));

          return {
            ...obj,
            box: [clampedCx, clampedCy, newW, newH] as [number, number, number, number],
          };
        })
      );
    }
  };

  // Mouse up handler
  const handleMouseUp = async () => {
    // Stop panning
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    // Capture current state before resetting
    const currentDragState = dragState;
    const currentNewBoxPreview = newBoxPreview;

    // Reset drag state immediately to stop further dragging
    setDragState({ type: null });

    if (currentDragState.type === 'create' && currentNewBoxPreview) {
      const { x1, y1, x2, y2 } = currentNewBoxPreview;

      // Convert canvas coords to image coords
      const img1 = canvasToImage(x1, y1);
      const img2 = canvasToImage(x2, y2);

      const minX = Math.min(img1.x, img2.x);
      const minY = Math.min(img1.y, img2.y);
      const boxW = Math.abs(img2.x - img1.x);
      const boxH = Math.abs(img2.y - img1.y);

      // Only create if box is large enough (in image pixels)
      if (boxW >= 10 && boxH >= 10) {
        const propW = boxW / imageSize.width;
        const propH = boxH / imageSize.height;
        const propCx = (minX / imageSize.width) + propW / 2;
        const propCy = (minY / imageSize.height) + propH / 2;

        await createObject(propCx, propCy, propW, propH, newObjectType);
      }

      setNewBoxPreview(null);
      setCreatingObject(false);
    }

    if (currentDragState.type === 'corner' && currentDragState.objectId) {
      const obj = objects.find((o) => o.id === currentDragState.objectId);
      if (obj) {
        await saveObjectBox(obj);
      }
    }
  };

  // Mouse wheel handler for zoom (zooms towards mouse position)
  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const pos = getMousePos(e);
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * zoomFactor));

    // Zoom towards mouse position
    const zoomRatio = newZoom / zoom;
    setPanOffset({
      x: pos.x - (pos.x - panOffset.x) * zoomRatio,
      y: pos.y - (pos.y - panOffset.y) * zoomRatio,
    });
    setZoom(newZoom);
  };

  // Reset view to fit image in canvas
  const handleResetView = () => {
    if (imageSize.width === 0 || canvasSize.width === 0) {
      setZoom(1);
      setPanOffset({ x: 0, y: 0 });
      return;
    }

    const scaleX = canvasSize.width / imageSize.width;
    const scaleY = canvasSize.height / imageSize.height;
    const fitZoom = Math.min(scaleX, scaleY, MAX_ZOOM);

    const scaledWidth = imageSize.width * fitZoom;
    const scaledHeight = imageSize.height * fitZoom;
    const offsetX = (canvasSize.width - scaledWidth) / 2;
    const offsetY = (canvasSize.height - scaledHeight) / 2;

    setZoom(fitZoom);
    setPanOffset({ x: offsetX, y: offsetY });
  };

  // Create new object
  const createObject = async (cx: number, cy: number, w: number, h: number, type: DetectionObjectType) => {
    setSaving(true);
    setError(null);

    try {
      const response = await api.post<{ results: {id: string} }>('detection/object/create', {
        image: detection.id,
        box: [cx, cy, w, h],
        type,
        angle: null,
      });

      // API only returns the ID, construct the object ourselves
      const newObjectId = response.data.results.id;
      const newObject: DetectionObject = {
        id: newObjectId,
        box: [cx, cy, w, h],
        image: detection.id,
        type,
        angle: null,
      };

      const newObjects = [...objects, newObject];
      setObjects(newObjects);
      setSelectedObjectId(newObjectId);
      onObjectsUpdated(detection.id, newObjects);
    } catch (err) {
      console.error('Failed to create object:', err);
      setError('Failed to create object');
    } finally {
      setSaving(false);
    }
  };

  // Save object box
  const saveObjectBox = async (obj: DetectionObject) => {
    setSaving(true);
    setError(null);

    try {
      await api.patch(`detection/object/patch/${obj.id}`, {
        box: obj.box,
      });
      onObjectsUpdated(detection.id, objects);
    } catch (err) {
      console.error('Failed to save object:', err);
      setError('Failed to save object');
    } finally {
      setSaving(false);
    }
  };

  // Delete object
  const handleDeleteObject = useCallback(async () => {
    if (!selectedObjectId) return;

    setSaving(true);
    setError(null);

    try {
      await api.delete(`detection/object/delete/${selectedObjectId}`);
      const newObjects = objects.filter((o) => o.id !== selectedObjectId);
      setObjects(newObjects);
      setSelectedObjectId(null);
      onObjectsUpdated(detection.id, newObjects);
    } catch (err) {
      console.error('Failed to delete object:', err);
      setError('Failed to delete object');
    } finally {
      setSaving(false);
    }
  }, [selectedObjectId, objects, detection.id, onObjectsUpdated]);

  // Update object angle
  const handleAngleChange = useCallback(async (newAngle: number | null) => {
    if (!selectedObjectId) return;

    setSaving(true);
    setError(null);

    try {
      await api.patch(`detection/object/patch/${selectedObjectId}`, {
        angle: newAngle,
      });

      const newObjects = objects.map((o) =>
        o.id === selectedObjectId ? { ...o, angle: newAngle } : o
      );
      setObjects(newObjects);
      onObjectsUpdated(detection.id, newObjects);
    } catch (err) {
      console.error('Failed to update angle:', err);
      setError('Failed to update angle');
    } finally {
      setSaving(false);
    }
  }, [selectedObjectId, objects, detection.id, onObjectsUpdated]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'Delete' && selectedObjectId && !saving) {
        e.preventDefault();
        handleDeleteObject();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (editingAngle) {
          // Cancel angle editing without saving
          setEditingAngle(false);
          setPreviewAngle(null);
        } else if (selectedObjectId || creatingObject) {
          // Deselect object or cancel creation mode
          setSelectedObjectId(null);
          setCreatingObject(false);
        } else {
          // Nothing selected, close the editor
          onClose();
        }
      } else if (!saving) {
        // Object creation shortcuts
        const key = e.key.toLowerCase();
        if (key === 'v') {
          e.preventDefault();
          setNewObjectType('vehicle');
          setCreatingObject(true);
          setSelectedObjectId(null);
        } else if (key === 'p') {
          e.preventDefault();
          setNewObjectType('plate');
          setCreatingObject(true);
          setSelectedObjectId(null);
        } else if (key === 'b') {
          e.preventDefault();
          setNewObjectType('bike');
          setCreatingObject(true);
          setSelectedObjectId(null);
        } else if (key === 'h') {
          e.preventDefault();
          setNewObjectType('hazard');
          setCreatingObject(true);
          setSelectedObjectId(null);
        } else if (key === 'a') {
          // Toggle angle editing for vehicle/bike objects
          const selectedObj = objects.find((o) => o.id === selectedObjectId);
          if (selectedObj && (selectedObj.type === 'vehicle' || selectedObj.type === 'bike')) {
            e.preventDefault();
            if (editingAngle) {
              // Save the preview angle and exit editing mode
              if (previewAngle !== null) {
                handleAngleChange(previewAngle);
              }
              setEditingAngle(false);
              setPreviewAngle(null);
            } else {
              // Enter angle editing mode
              setEditingAngle(true);
              setPreviewAngle(selectedObj.angle);
              setCreatingObject(false);
            }
          }
        } else if (key === 'f') {
          // Finish: close editor and optionally mark as completed (if in task context)
          e.preventDefault();
          if (onFinish) {
            onFinish();
          }
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedObjectId, creatingObject, saving, editingAngle, previewAngle, objects, handleDeleteObject, handleAngleChange, onClose, onFinish]);

  // Update object type
  const handleTypeChange = async (newType: DetectionObjectType) => {
    if (!selectedObjectId) return;

    const obj = objects.find((o) => o.id === selectedObjectId);
    if (!obj) return;

    setSaving(true);
    setError(null);

    try {
      await api.patch(`detection/object/patch/${selectedObjectId}`, {
        type: newType,
        angle: newType === 'vehicle' || newType === 'bike' ? obj.angle : null,
      });

      const newObjects = objects.map((o) =>
        o.id === selectedObjectId
          ? { ...o, type: newType, angle: newType === 'vehicle' || newType === 'bike' ? o.angle : null }
          : o
      );
      setObjects(newObjects);
      onObjectsUpdated(detection.id, newObjects);
    } catch (err) {
      console.error('Failed to update type:', err);
      setError('Failed to update type');
    } finally {
      setSaving(false);
    }
  };

  const selectedObject = objects.find((o) => o.id === selectedObjectId);
  const showAngleField = selectedObject && (selectedObject.type === 'vehicle' || selectedObject.type === 'bike');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown
      slotProps={{ paper: {sx: { height: '90vh', maxHeight: '900px' }} }}
    >
      <DialogTitle variant="h6" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Object Editor
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 0.25))} size="small">
            <ZoomOutIcon />
          </IconButton>
          <Typography sx={{ alignSelf: 'center', minWidth: 50, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <IconButton onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 0.25))} size="small">
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleResetView} size="small">
            <RestartAltIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, overflow: 'hidden' }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          Click to select. Drag corners to resize. Scroll to zoom. Middle-drag to pan. Keys: V/P/B/H to add, A to set angle (vehicle/bike), Del to delete, F to finish, Esc to deselect.
        </Typography>

        {/* Canvas Container */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            bgcolor: '#333',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {image ? (
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
              onContextMenu={(e) => e.preventDefault()}
              style={{
                display: 'block',
                cursor: isPanning ? 'grabbing' : (creatingObject || editingAngle) ? 'crosshair' : 'default',
              }}
              role="application"
              aria-label="Detection object editor. Use V, P, B, H keys to create objects. Delete key to remove selected object. Scroll to zoom, middle-click to pan."
              tabIndex={0}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>

        {/* Object Controls Panel */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Selected Object Info */}
            {selectedObject ? (
              <>
                <Typography variant="body2" sx={{ alignSelf: 'center', mr: 2 }}>
                  Selected: <strong>{selectedObject.id.substring(0, 8)}...</strong>
                </Typography>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedObject.type}
                    onChange={(e) => handleTypeChange(e.target.value as DetectionObjectType)}
                    label="Type"
                    disabled={saving}
                  >
                    {OBJECT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              bgcolor: OBJECT_TYPE_COLORS[type],
                              borderRadius: '2px',
                            }}
                          />
                          {type}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {showAngleField && (
                  <TextField
                    size="small"
                    label="Angle (0-360)"
                    type="number"
                    value={selectedObject.angle ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? parseInt(e.target.value, 10) : null;
                      if (val === null || (val >= 0 && val <= 360)) {
                        handleAngleChange(val);
                      }
                    }}
                    inputProps={{ min: 0, max: 360 }}
                    sx={{ width: 120 }}
                    disabled={saving}
                  />
                )}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedObjectId(null)}
                >
                  Deselect
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteObject}
                  disabled={saving}
                >
                  Delete
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                No object selected
              </Typography>
            )}

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Add Object Controls */}
            {creatingObject ? (
              <>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>New Type</InputLabel>
                  <Select
                    value={newObjectType}
                    onChange={(e) => setNewObjectType(e.target.value as DetectionObjectType)}
                    label="New Type"
                  >
                    {OBJECT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              bgcolor: OBJECT_TYPE_COLORS[type],
                              borderRadius: '2px',
                            }}
                          />
                          {type}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" onClick={() => setCreatingObject(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreatingObject(true)}
                sx={{ bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#7b1fa2' } }}
                disabled={saving}
              >
                Add Object
              </Button>
            )}
          </Box>
        </Paper>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {OBJECT_TYPES.map((type) => (
            <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: OBJECT_TYPE_COLORS[type],
                  borderRadius: '2px',
                }}
              />
              <Typography variant="caption">{type}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        {saving && <CircularProgress size={20} sx={{ mr: 2 }} />}
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetectionObjectEditor;
